import { useEffect, useMemo, useState, type ChangeEvent, type ReactElement } from 'react';
import type { AircraftType, AirportView, FleetAircraftView } from '../../../domain/models/contracts';
import type {
  AircraftRecommendation,
  CompetitionLevel,
  RouteMarketPreview,
  RouteMarketPreviewReady,
} from '../../../domain/routing/routePlannerEngine';
import { formatInt, formatUsd } from '../../format';
import type { PendingScheduleLeg } from '../FleetScheduleGrid';
import { CABIN_SEGMENT_COLORS, DemandStackedBar } from './DemandStackedBar';
import './RoutePlannerPanel.css';

export interface RoutePlannerPanelProps {
  /** Airports offered as suggestions. The worker accepts any code in its full database. */
  readonly airports: readonly AirportView[];
  readonly aircraftTypes: readonly AircraftType[];
  readonly fleet: readonly FleetAircraftView[];
  /** Latest worker answer; the panel only uses it when it matches the selected pair. */
  readonly preview: RouteMarketPreview | null;
  readonly onRequestPreview: (originId: string, destinationId: string) => void;
  /** A pair to load into the inputs (from the market survey). `nonce` makes repeat requests for the same pair apply again. */
  readonly requestedPair?: { readonly originId: string; readonly destinationId: string; readonly nonce: number } | null;
  /** Hands the route and chosen aircraft to the Gantt scheduler. */
  readonly onScheduleLeg: (leg: PendingScheduleLeg) => void;
}

const IATA_PATTERN = /^[A-Z]{3}$/;

const COMPETITION_LABEL: Readonly<Record<CompetitionLevel, string>> = {
  UNCONTESTED: 'Uncontested',
  MODERATELY_SERVED: 'Moderately served',
  HIGHLY_SATURATED: 'Highly saturated',
};

const FARE_ROWS: ReadonlyArray<{ readonly key: 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST'; readonly label: string }> = [
  { key: 'ECONOMY', label: 'Economy' },
  { key: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
  { key: 'BUSINESS', label: 'Business' },
  { key: 'FIRST', label: 'First' },
];

function formatBlock(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours}h ${String(rest).padStart(2, '0')}m`;
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

interface EligibleAircraft {
  readonly aircraft: FleetAircraftView;
  readonly typeName: string;
  readonly atOrigin: boolean;
}

export function RoutePlannerPanel({
  airports,
  aircraftTypes,
  fleet,
  preview,
  onRequestPreview,
  onScheduleLeg,
  requestedPair = null,
}: RoutePlannerPanelProps): ReactElement {
  const [originInput, setOriginInput] = useState<string>('');
  const [destinationInput, setDestinationInput] = useState<string>('');
  const [chosenReg, setChosenReg] = useState<string>('');

  useEffect(() => {
    if (requestedPair === null) return;
    setOriginInput(requestedPair.originId);
    setDestinationInput(requestedPair.destinationId);
    setChosenReg('');
  }, [requestedPair]);

  const originCode = originInput.trim().toUpperCase();
  const destinationCode = destinationInput.trim().toUpperCase();
  const pairReady =
    IATA_PATTERN.test(originCode) && IATA_PATTERN.test(destinationCode) && originCode !== destinationCode;

  useEffect(() => {
    if (pairReady) onRequestPreview(originCode, destinationCode);
  }, [pairReady, originCode, destinationCode, onRequestPreview]);

  const airportsById = useMemo(() => new Map(airports.map((airport) => [airport.id, airport] as const)), [airports]);
  const typesById = useMemo(() => new Map(aircraftTypes.map((type) => [type.id, type] as const)), [aircraftTypes]);

  const current = pairReady && preview !== null && preview.originId === originCode && preview.destinationId === destinationCode
    ? preview
    : null;
  const ready: RouteMarketPreviewReady | null = current !== null && current.status === 'READY' ? current : null;

  // Fleet aircraft that can legally fly the pair. Ones already at the origin come first.
  const eligible = useMemo<readonly EligibleAircraft[]>(() => {
    if (ready === null) return [];
    const originRunway = airportsById.get(originCode)?.runwayLengthMeters;
    const destinationRunway = airportsById.get(destinationCode)?.runwayLengthMeters;
    const knownRunways = [originRunway, destinationRunway].filter((runway): runway is number => runway !== undefined);
    const runwayLimit = knownRunways.length > 0 ? Math.min(...knownRunways) : Number.POSITIVE_INFINITY;
    return fleet
      .flatMap((aircraft): EligibleAircraft[] => {
        const type = typesById.get(aircraft.typeId);
        if (type === undefined) return [];
        if (type.maxRangeKm < ready.distanceKm) return [];
        if (type.minRunwayRequiredMeters > runwayLimit) return [];
        return [{ aircraft, typeName: type.name, atOrigin: aircraft.locationAirportId === originCode }];
      })
      .sort((a, b) => Number(b.atOrigin) - Number(a.atOrigin) || a.aircraft.registration.localeCompare(b.aircraft.registration));
  }, [ready, fleet, typesById, airportsById, originCode, destinationCode]);

  const selectedReg = eligible.some((option) => option.aircraft.registration === chosenReg)
    ? chosenReg
    : (eligible[0]?.aircraft.registration ?? '');
  const selected = eligible.find((option) => option.aircraft.registration === selectedReg);

  const onOriginChange = (event: ChangeEvent<HTMLInputElement>): void => setOriginInput(event.target.value);
  const onDestinationChange = (event: ChangeEvent<HTMLInputElement>): void => setDestinationInput(event.target.value);

  const originName = airportsById.get(originCode)?.name;
  const destinationName = airportsById.get(destinationCode)?.name;

  return (
    <div className="route-planner">
      <header className="rp-header">
        <div>
          <h2 className="rp-title">Market Opportunity Planner</h2>
          <p className="rp-subtitle">
            Preview a pair you do not fly yet: demand, competition, fares and what each aircraft could earn.
          </p>
        </div>
        <div className="rp-selectors">
          <label className="rp-field">
            <span className="rp-field-label">Origin</span>
            <input
              className="rp-input"
              type="text"
              list="rp-airport-codes"
              value={originInput}
              onChange={onOriginChange}
              placeholder="LHR"
              maxLength={3}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <span className="rp-arrow" aria-hidden="true">→</span>
          <label className="rp-field">
            <span className="rp-field-label">Destination</span>
            <input
              className="rp-input"
              type="text"
              list="rp-airport-codes"
              value={destinationInput}
              onChange={onDestinationChange}
              placeholder="JFK"
              maxLength={3}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <datalist id="rp-airport-codes">
            {airports.map((airport) => (
              <option key={airport.id} value={airport.id} label={`${airport.name} · ${airport.city}, ${airport.country}`} />
            ))}
          </datalist>
        </div>
      </header>

      {!pairReady && (
        <p className="rp-hint">
          {originCode !== '' && destinationCode !== '' && originCode === destinationCode
            ? 'Pick two different airports.'
            : 'Enter two three-letter airport codes to preview a route.'}
        </p>
      )}

      {pairReady && current === null && <p className="rp-hint">Evaluating the market…</p>}

      {current !== null && current.status === 'REJECTED' && <p className="rp-error">{current.message}</p>}

      {ready !== null && (
        <div className="rp-body">
          <section className="rp-card rp-summary" aria-label="Route summary">
            <div className="rp-stat">
              <span className="rp-stat-label">Route</span>
              <span className="rp-stat-value">
                {ready.originId} → {ready.destinationId}
              </span>
              <span className="rp-stat-note">
                {[originName, destinationName].filter((name): name is string => name !== undefined).join(' → ') || 'Airport names unavailable'}
              </span>
            </div>
            <div className="rp-stat">
              <span className="rp-stat-label">Distance</span>
              <span className="rp-stat-value">{formatInt(ready.distanceKm)} km</span>
            </div>
            <div className="rp-stat">
              <span className="rp-stat-label">Profit potential</span>
              <span className="rp-stat-value rp-accent">{formatUsd(ready.profitPotentialUSDPerDay)}/day</span>
              <span className="rp-stat-note">Best aircraft after costs and lease rent, both directions</span>
            </div>
          </section>

          <section className="rp-card" aria-label="Block time by aircraft segment">
            <h3 className="rp-card-title">Block time by segment</h3>
            <div className="rp-blocks">
              {ready.blockTimes.map((block) => (
                <div className="rp-block" key={block.segment}>
                  <span className="rp-block-segment">{block.segment.charAt(0) + block.segment.slice(1).toLowerCase()}</span>
                  <span className="rp-block-time">{formatBlock(block.blockMinutes)}</span>
                  <span className="rp-block-note">{formatInt(block.cruiseSpeedKmh)} km/h block speed</span>
                </div>
              ))}
            </div>
          </section>

          <section className="rp-card" aria-label="Origin to destination demand">
            <h3 className="rp-card-title">O&amp;D demand per day</h3>
            <DemandStackedBar
              unit="passengers a day"
              segments={[
                { key: 'Y', label: 'Economy', value: ready.demand.leisurePassengersPerDay, color: CABIN_SEGMENT_COLORS.Y },
                { key: 'J', label: 'Business', value: ready.demand.businessPassengersPerDay, color: CABIN_SEGMENT_COLORS.J },
              ]}
              popoverTitle="Competitor capacity per day"
              popoverRows={[
                { label: `Rival seats (${ready.competition.competitorAirlines} carriers)`, value: ready.competition.competitorSeatsPerDay, color: '#6b7280' },
                { label: 'Rival flights', value: Math.round(ready.competition.competitorFlightsPerDay), color: '#9ca3af' },
              ]}
            />
            <p className="rp-note">{formatInt(Math.round(ready.demand.totalPassengersPerDay))} passengers a day in this direction.</p>
          </section>

          <section className="rp-card" aria-label="Market competition">
            <h3 className="rp-card-title">Market competition</h3>
            <div
              className="rp-gauge"
              role="meter"
              aria-label="Competitor seat pressure"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(ready.competition.pressure * 100)}
              aria-valuetext={COMPETITION_LABEL[ready.competition.level]}
            >
              <div className="rp-gauge-track">
                <span className="rp-gauge-needle" style={{ left: `${ready.competition.pressure * 100}%` }} />
              </div>
              <div className="rp-gauge-scale">
                <span>Uncontested</span>
                <span>Moderately served</span>
                <span>Highly saturated</span>
              </div>
            </div>
            <p className={`rp-level rp-level--${ready.competition.level.toLowerCase().replace('_', '-')}`}>
              {COMPETITION_LABEL[ready.competition.level]}
            </p>
            <dl className="rp-facts">
              <div>
                <dt>Rival carriers</dt>
                <dd>{ready.competition.competitorAirlines}</dd>
              </div>
              <div>
                <dt>Rival flights / day</dt>
                <dd>{formatInt(Math.round(ready.competition.competitorFlightsPerDay))}</dd>
              </div>
              <div>
                <dt>Rival seats / day</dt>
                <dd>{formatInt(ready.competition.competitorSeatsPerDay)}</dd>
              </div>
              <div>
                <dt>Your flights / day</dt>
                <dd>{formatInt(Math.round(ready.competition.ownFlightsPerDay))}</dd>
              </div>
            </dl>
          </section>

          <section className="rp-card" aria-label="Suggested fares">
            <h3 className="rp-card-title">Suggested fares</h3>
            <table className="rp-table">
              <thead>
                <tr>
                  <th scope="col">Cabin</th>
                  <th scope="col" className="rp-num">Reference</th>
                  <th scope="col" className="rp-num">Suggested</th>
                </tr>
              </thead>
              <tbody>
                {FARE_ROWS.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    <td className="rp-num">{formatUsd(ready.referenceFaresUSD[row.key])}</td>
                    <td className="rp-num">{formatUsd(ready.suggestedFaresUSD[row.key])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="rp-note">Suggested fares include a {formatPct(ready.competitionDiscountPct)} competition discount.</p>
          </section>

          <section className="rp-card rp-wide" aria-label="Recommended aircraft">
            <h3 className="rp-card-title">Recommended aircraft</h3>
            {ready.recommendedAircraft.length === 0 ? (
              <p className="rp-note">No aircraft in the catalogue can fly this pair with a daily rotation.</p>
            ) : (
              <table className="rp-table">
                <thead>
                  <tr>
                    <th scope="col">Aircraft</th>
                    <th scope="col" className="rp-num">Block</th>
                    <th scope="col" className="rp-num">Dep / day</th>
                    <th scope="col" className="rp-num">Seats</th>
                    <th scope="col" className="rp-num">Cabin</th>
                    <th scope="col" className="rp-num">Pax / day each way</th>
                    <th scope="col" className="rp-num">Load</th>
                    <th scope="col" className="rp-num">Revenue / day</th>
                    <th scope="col" className="rp-num">Cost / day</th>
                    <th scope="col" className="rp-num">Profit / day</th>
                  </tr>
                </thead>
                <tbody>
                  {ready.recommendedAircraft.map((option: AircraftRecommendation) => (
                    <tr key={option.typeId}>
                      <th scope="row">
                        {option.name}
                        <span className="rp-segment">{option.segment.toLowerCase()}</span>
                      </th>
                      <td className="rp-num">{formatBlock(option.blockMinutes)}</td>
                      <td className="rp-num">{option.departuresPerDay}</td>
                      <td className="rp-num">{formatInt(option.seatsPerFlight)}</td>
                      <td className="rp-num">
                        {option.cabin.businessSeats > 0
                          ? `${formatInt(option.cabin.businessSeats)} J / ${formatInt(option.cabin.economySeats + option.cabin.premiumEconomySeats + option.cabin.firstSeats)} Y`
                          : 'All economy'}
                      </td>
                      <td className="rp-num">{formatInt(Math.round(option.passengersPerDay))}</td>
                      <td className="rp-num">{formatPct(option.loadFactorPct)}</td>
                      <td className="rp-num">{formatUsd(option.dailyRevenueUSD)}</td>
                      <td className="rp-num">{formatUsd(option.dailyCostUSD)}</td>
                      <td className="rp-num rp-accent">{formatUsd(option.dailyProfitUSD)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="rp-assign">
              {eligible.length === 0 ? (
                <p className="rp-note">
                  {fleet.length === 0
                    ? 'No aircraft yet. Acquire one on Fleet & Acquisition first.'
                    : 'No aircraft in your fleet can fly this pair.'}
                </p>
              ) : (
                <>
                  <label className="rp-field rp-field--grow">
                    <span className="rp-field-label">Aircraft</span>
                    <select
                      className="rp-input"
                      value={selectedReg}
                      onChange={(event) => setChosenReg(event.target.value)}
                    >
                      {eligible.map((option) => (
                        <option key={option.aircraft.registration} value={option.aircraft.registration}>
                          {option.aircraft.registration} · {option.typeName} · {option.atOrigin ? `at ${originCode}` : `at ${option.aircraft.locationAirportId}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary rp-assign-button"
                    disabled={selected === undefined}
                    onClick={() => onScheduleLeg({ registration: selectedReg, destinationId: destinationCode })}
                  >
                    Assign Aircraft &amp; Schedule Leg
                  </button>
                </>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
